'use client'

import { Button } from "@/components/ui/button"
import { ArrowRight, Play } from "lucide-react"
import Link from "next/link"
import { useRouter } from "next/navigation"
import { useLanguage } from "@/lib/i18n/language-provider"
import { ScrollReveal } from "@/components/scroll-reveal"
import posthog from "posthog-js"

export function Hero() {
  const router = useRouter()
  const { t } = useLanguage()

  const handleStartFreeClick = () => {
    posthog.capture('signup_flow:hero_cta_click', {
      button_text: t('landing.hero.startFree'),
      button_location: 'hero',
    })
    router.push('/grid')
  }

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-16">
      {/* Animated color blobs in background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-[10%] w-96 h-96 bg-[#3b82f6] rounded-full blur-[128px] opacity-10 animate-[pulse_8s_ease-in-out_infinite]" />
        <div className="absolute top-[20%] right-[15%] w-80 h-80 bg-[#8b5cf6] rounded-full blur-[128px] opacity-10 animate-[pulse_8s_ease-in-out_infinite_1s]" />
        <div className="absolute bottom-[30%] left-[20%] w-72 h-72 bg-[#ef4444] rounded-full blur-[128px] opacity-10 animate-[pulse_8s_ease-in-out_infinite_2s]" />
        <div className="absolute bottom-[10%] right-[10%] w-80 h-80 bg-[#22c55e] rounded-full blur-[128px] opacity-10 animate-[pulse_8s_ease-in-out_infinite_3s]" />
        <div className="absolute top-[50%] left-[50%] w-64 h-64 bg-[#eab308] rounded-full blur-[128px] opacity-10 animate-[pulse_8s_ease-in-out_infinite_4s]" />
      </div>

      {/* Grid backgrounds */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff12_2px,transparent_2px),linear-gradient(to_bottom,#ffffff12_2px,transparent_2px)] bg-[size:80px_80px]" />
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff08_1px,transparent_1px),linear-gradient(to_bottom,#ffffff08_1px,transparent_1px)] bg-[size:20px_20px]" />

      {/* Decorative corners */}
      <div className="absolute top-4 left-4 w-12 h-12 border-t-2 border-l-2 border-foreground/30" />
      <div className="absolute top-4 right-4 w-12 h-12 border-t-2 border-r-2 border-foreground/30" />
      <div className="absolute bottom-4 left-4 w-12 h-12 border-b-2 border-l-2 border-foreground/30" />
      <div className="absolute bottom-4 right-4 w-12 h-12 border-b-2 border-r-2 border-foreground/30" />

      <div className="container relative z-10 px-4 md:px-6 max-w-7xl mx-auto">
        <div className="flex flex-col items-center text-center space-y-8 md:space-y-12 max-w-5xl mx-auto">
          {/* Main title */}
          <ScrollReveal animation="zoom-blur" duration={1.2}>
            <div className="relative">
              <div className="absolute inset-0 bg-foreground/10 blur-3xl opacity-50" />
              <div className="relative border-2 md:border-4 border-foreground/20 p-4 md:p-8 bg-background/80 backdrop-blur-sm">
                <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff08_1px,transparent_1px),linear-gradient(to_bottom,#ffffff08_1px,transparent_1px)] bg-[size:10px_10px]" />
                <h1 className="relative text-4xl sm:text-6xl md:text-8xl lg:text-9xl font-bold tracking-tighter text-foreground">
                  VisualGrid
                </h1>
              </div>
            </div>
          </ScrollReveal>

          {/* Subtitle */}
          <ScrollReveal animation="blur-in" delay={0.2} duration={1}>
            <h2 className="text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-bold tracking-tight text-balance px-2">
              {t('landing.hero.title')}
              <br />
              <span className="text-foreground/50">
                {t('landing.hero.subtitle')}
              </span>
            </h2>
          </ScrollReveal>

          {/* Description */}
          <ScrollReveal animation="fade-in" delay={0.4} duration={1}>
            <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-muted-foreground max-w-3xl leading-relaxed px-4">
              {t('landing.hero.description')}
            </p>
          </ScrollReveal>

          {/* CTA Buttons */}
          <ScrollReveal animation="fade-in" delay={0.6} duration={1} className="w-full flex justify-center px-4">
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-4 w-full sm:w-auto">
              <Button
                size="lg"
                onClick={handleStartFreeClick}
                className="text-base sm:text-lg px-8 sm:px-12 py-6 sm:py-8 h-auto bg-foreground text-background hover:bg-foreground/90 group transition-all w-full sm:w-auto"
              >
                {t('landing.hero.startFree')}
                <ArrowRight className="ml-2 sm:ml-3 h-5 sm:h-6 w-5 sm:w-6 transition-transform group-hover:translate-x-1" />
              </Button>
              <Link href="/pro" className="w-full sm:w-auto">
                <Button
                  size="lg"
                  variant="outline"
                  className="text-base sm:text-lg px-8 sm:px-12 py-6 sm:py-8 h-auto border-2 border-foreground/20 bg-background/80 backdrop-blur-sm hover:bg-foreground/10 hover:border-foreground/40 transition-all text-foreground group w-full"
                >
                  <Play className="mr-2 sm:mr-3 h-4 sm:h-5 w-4 sm:w-5 transition-transform group-hover:scale-110" />
                  {t('landing.hero.discoverPro')}
                </Button>
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </div>
    </section>
  )
}

